import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { cacher } from 'app/cacheUtils/cacheIndex';
import { queryApi } from './queryApi';
import { RevenueDto } from 'features/revenue/types/revenueDto';
import { RootState } from 'app/store';
import {
  onNumberOfRevenueItemsUploadedChanged,
  onNumberOfOpportunityItemsUploadedChanged
} from 'features/upload/uploads.slice';
import { OpportunityDto } from 'features/opportunities/types/opportunityDto';

export const publicCommandApi = createApi({
  reducerPath: 'publicCommandApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_PUBLIC_COMMAND_API_URL,
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const token: string | null = (getState() as RootState).auth.accessToken;
      if (token != null) {
        headers.set('authorization', `Bearer ${token}`);
      }

      return headers;
    }
  }),
  tagTypes: [...cacher.defaultTags],
  endpoints: (builder) => ({
    uploadRevenues: builder.mutation<
      unknown,
      { siteId: string; revenues: RevenueDto[] }
    >({
      async queryFn(request, api, extraOptions, baseQuery) {
        for (const revenue of request.revenues) {
          const itemNumber = request.revenues.indexOf(revenue) + 1;

          const uploadPromise = baseQuery({
            url: `${request.siteId}/revenue/upload`,
            method: 'PUT',
            body: revenue
          });

          const result = await uploadPromise;

          const error = result.error?.data as any;
          const isUploadSuccessful = error == null;

          if (!isUploadSuccessful) {
            const errorData = {
              isUploadSuccessful: false,
              errors: error.Errors,
              itemNumberFailure: itemNumber
            };

            return {
              data: errorData
            };
          }

          api.dispatch(onNumberOfRevenueItemsUploadedChanged(itemNumber));
        }

        api.dispatch(queryApi.util.invalidateTags(['Revenue']));

        return { data: { isUploadSuccessful: true } };
      }
    }),
    uploadOpportunities: builder.mutation<
      unknown,
      { siteId: string; opportunities: OpportunityDto[] }
    >({
      async queryFn(request, api, extraOptions, baseQuery) {
        for (const opportunity of request.opportunities) {
          const itemNumber = request.opportunities.indexOf(opportunity) + 1;

          const uploadPromise = baseQuery({
            url: `${request.siteId}/opportunity/upload`,
            method: 'PUT',
            body: opportunity
          });

          const result = await uploadPromise;

          const error = result.error?.data as any;
          const isUploadSuccessful = error == null;

          if (!isUploadSuccessful) {
            const errorData = {
              isUploadSuccessful: false,
              errors: error.Errors,
              itemNumberFailure: itemNumber
            };

            return {
              data: errorData
            };
          }

          api.dispatch(onNumberOfOpportunityItemsUploadedChanged(itemNumber));
        }

        api.dispatch(queryApi.util.invalidateTags(['Opportunities']));

        return { data: { isUploadSuccessful: true } };
      }
    })
  })
});

export const { useUploadRevenuesMutation, useUploadOpportunitiesMutation } =
  publicCommandApi;
